import {ApplicationType} from 'ApplicationEntry/models/ApplicationType';
import * as G from 'ApplicationEntry/models/ProductsRiders/ICRTN/GroupICRTN';
import {ICRTN, matchICRTNByType} from 'ApplicationEntry/models/ProductsRiders/ICRTN/ICRTN';
import * as NY from 'ApplicationEntry/models/ProductsRiders/ICRTN/NewYorkICRTN';
import * as O from 'ApplicationEntry/models/ProductsRiders/ICRTN/OrdinaryICRTN';
import {Plan} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {Async, Loading} from 'Core/models/Async';
import {NonEmptyString} from 'Core/models/EmptyString';
import {createAction} from 'Shared/utilities/actionCreator';
import {reverseEnum} from 'Shared/utilities/enumUtility';

export enum ICRTNActionType {
  CHANGE_PRODUCT_TYPE = 'icrtn/CHANGE_PRODUCT_TYPE',
  CHANGE_PLAN_TYPE = 'icrtn/CHANGE_PLAN_TYPE',
  CHANGE_PREMIUM = 'icrtn/CHANGE_PREMIUM',
  CHANGE_CALCULATED_PREMIUM = 'icrtn/CHANGE_CALCULATED_PREMIUM',
  CHANGE_CWA = 'icrtn/CHANGE_CWA',
  CHANGE_GROUP_TRUST = 'icrtn/CHANGE_GROUP_TRUST',
  CHANGE_SECTION125 = 'icrtn/CHANGE_SECTION125',
  CHANGE_REPLACEMENT = 'icrtn/CHANGE_REPLACEMENT',
  CHANGE_ORIGINAL_AGE = 'icrtn/CHANGE_ORIGINAL_AGE',
  CHANGE_RERATE = 'icrtn/CHANGE_RERATE',
  CHANGE_POLICY_NUMBER = 'icrtn/CHANGE_POLICY_NUMBER',
  CHANGE_ICR_UNITS = 'icrtn/CHANGE_ICR_UNITS',
  CHANGE_ROP_RIDER = 'icrtn/CHANGE_ROP_RIDER',
  CHANGE_OVERRIDE_BILL_FORM = 'icrtn/CHANGE_OVERRIDE_BILL_FORM',
  GET_ICRTN_PRODUCTS = 'icrtn/GET_ICRTN_PRODUCTS',
  CHANGE_ICRTN_PRODUCTS = 'icrtn/CHANGE_ICRTN_PRODUCTS',
  GET_ICRTN_ICR_UNITS = 'icrtn/GET_ICRTN_ICR_UNITS',
  CHANGE_ICRTN_ICR_UNITS = 'icrtn/CHANGE_ICRTN_ICR_UNITS',
}

export const isICRTNAction = (action: any): action is ICRTNAction => action.type in reverseEnum(ICRTNActionType);

export const changeProductType = (productType: string) =>
  createAction(ICRTNActionType.CHANGE_PRODUCT_TYPE, {productType});
export const changePlanType = (planType: string) => createAction(ICRTNActionType.CHANGE_PLAN_TYPE, {planType});
export const changeICRUnits = (icrUnits: string) => createAction(ICRTNActionType.CHANGE_ICR_UNITS, {icrUnits});
export const changeROPRider = (ropRider: boolean) => createAction(ICRTNActionType.CHANGE_GROUP_TRUST, {ropRider});
export const changePremium = (premium: string) => createAction(ICRTNActionType.CHANGE_PREMIUM, {premium});
export const changeCalculatedPremium = (calculatedPremium: string) =>
  createAction(ICRTNActionType.CHANGE_CALCULATED_PREMIUM, {calculatedPremium});
export const changeCWA = (cwa: string) => createAction(ICRTNActionType.CHANGE_CWA, {cwa});
export const changeGroupTrust = (groupTrust: boolean) => createAction(ICRTNActionType.CHANGE_GROUP_TRUST, {groupTrust});
export const changeSection125 = (section125: boolean) => createAction(ICRTNActionType.CHANGE_SECTION125, {section125});
export const changeReplacement = (replacement: boolean) =>
  createAction(ICRTNActionType.CHANGE_REPLACEMENT, {replacement});
export const changeOriginalAge = (originalAge: number) =>
  createAction(ICRTNActionType.CHANGE_ORIGINAL_AGE, {originalAge});
export const changeRerate = (rerate: boolean) => createAction(ICRTNActionType.CHANGE_RERATE, {rerate});
export const changePolicyNumber = (policyNumber: string) =>
  createAction(ICRTNActionType.CHANGE_POLICY_NUMBER, {policyNumber});
export const changeOverrideBillForm = (overrideBillForm: boolean) =>
  createAction(ICRTNActionType.CHANGE_OVERRIDE_BILL_FORM, {overrideBillForm});
export const getICRTNProducts = (applicationType: ApplicationType, family: string) =>
  createAction(ICRTNActionType.GET_ICRTN_PRODUCTS, {applicationType, family});
export const changeICRTNProducts = (products: Async<Plan[]>) =>
  createAction(ICRTNActionType.CHANGE_ICRTN_PRODUCTS, {products});
export const getICRTNICRUnits = (family: string, product: string) =>
  createAction(ICRTNActionType.GET_ICRTN_ICR_UNITS, {family, product});
export const changeICRTNUnits = (icrUnits: Async<string[]>) =>
  createAction(ICRTNActionType.CHANGE_ICRTN_ICR_UNITS, {icrUnits});

export type ICRTNAction =
  | ReturnType<typeof changeProductType>
  | ReturnType<typeof changePlanType>
  | ReturnType<typeof changeICRUnits>
  | ReturnType<typeof changeGroupTrust>
  | ReturnType<typeof changePremium>
  | ReturnType<typeof changeCalculatedPremium>
  | ReturnType<typeof changeCWA>
  | ReturnType<typeof changeGroupTrust>
  | ReturnType<typeof changeSection125>
  | ReturnType<typeof changeReplacement>
  | ReturnType<typeof changeOriginalAge>
  | ReturnType<typeof changeRerate>
  | ReturnType<typeof changePolicyNumber>
  | ReturnType<typeof changeOverrideBillForm>
  | ReturnType<typeof getICRTNProducts>
  | ReturnType<typeof changeICRTNProducts>
  | ReturnType<typeof getICRTNICRUnits>
  | ReturnType<typeof changeICRTNUnits>;

type ICRTNState = ICRTN;

export default (state: ICRTNState, action: ICRTNAction): ICRTNState => {
  switch (action.type) {
    case ICRTNActionType.CHANGE_PRODUCT_TYPE:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeProductType(action.payload.productType),
        Group: G.changeProductType(action.payload.productType),
        NewYork: NY.changeProductType(action.payload.productType),
      })(state);
    case ICRTNActionType.CHANGE_PLAN_TYPE:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changePlanType(action.payload.planType),
        Group: G.changePlanType(action.payload.planType),
        NewYork: NY.changePlanType(action.payload.planType),
      })(state);
    case ICRTNActionType.CHANGE_ICR_UNITS:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeICRUnits(action.payload.icrUnits),
        Group: G.changeICRUnits(action.payload.icrUnits),
        NewYork: NY.changeICRUnits(action.payload.icrUnits),
      })(state);
    case ICRTNActionType.CHANGE_PREMIUM:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changePremium(action.payload.premium),
        Group: G.changePremium(action.payload.premium),
        NewYork: NY.changePremium(action.payload.premium),
      })(state);
    case ICRTNActionType.CHANGE_CALCULATED_PREMIUM:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeCalculatedPremium(action.payload.calculatedPremium),
        Group: G.changeCalculatedPremium(action.payload.calculatedPremium),
        NewYork: NY.changeCalculatedPremium(action.payload.calculatedPremium),
      })(state);
    case ICRTNActionType.CHANGE_CWA:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeCWA(action.payload.cwa),
        Group: G.changeCWA(action.payload.cwa),
        NewYork: NY.changeCWA(action.payload.cwa),
      })(state);
    case ICRTNActionType.CHANGE_GROUP_TRUST:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeGroupTrust(action.payload.groupTrust),
        Group: G.changeGroupTrust(action.payload.groupTrust),
        NewYork: NY.changeGroupTrust(action.payload.groupTrust),
      })(state);
    case ICRTNActionType.CHANGE_SECTION125:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeSection125(action.payload.section125),
        Group: G.changeSection125(action.payload.section125),
        NewYork: NY.changeSection125(action.payload.section125),
      })(state);
    case ICRTNActionType.CHANGE_REPLACEMENT:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeReplacement(action.payload.replacement),
        Group: G.changeReplacement(action.payload.replacement),
        NewYork: NY.changeReplacement(action.payload.replacement),
      })(state);
    case ICRTNActionType.CHANGE_ORIGINAL_AGE:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeOriginalAge(action.payload.originalAge),
        Group: G.changeOriginalAge(action.payload.originalAge),
        NewYork: NY.changeOriginalAge(action.payload.originalAge),
      })(state);
    case ICRTNActionType.CHANGE_RERATE:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeReRate(action.payload.rerate),
        Group: G.changeReRate(action.payload.rerate),
        NewYork: NY.changeReRate(action.payload.rerate),
      })(state);
    case ICRTNActionType.CHANGE_POLICY_NUMBER:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
        Group: G.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
        NewYork: NY.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
      })(state);
    case ICRTNActionType.CHANGE_OVERRIDE_BILL_FORM:
      return matchICRTNByType<ICRTN>({
        Ordinary: O.changeOverrideBillForm(action.payload.overrideBillForm),
        Group: G.changeOverrideBillForm(action.payload.overrideBillForm),
        NewYork: NY.changeOverrideBillForm(action.payload.overrideBillForm),
      })(state);
    case ICRTNActionType.GET_ICRTN_PRODUCTS:
      return matchICRTNByType<ICRTN>({
        Group: G.changeProductList(new Loading()),
        Ordinary: O.changeProductList(new Loading()),
        NewYork: NY.changeProductList(new Loading()),
      })(state);
    case ICRTNActionType.CHANGE_ICRTN_PRODUCTS:
      return matchICRTNByType<ICRTN>({
        Group: G.changeProductList(action.payload.products),
        Ordinary: O.changeProductList(action.payload.products),
        NewYork: NY.changeProductList(action.payload.products),
      })(state);
    default:
      return state;
  }
};
